import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  ScrollView,
  FlatList,
  Platform,
  StyleSheet,
} from 'react-native';
import { Image } from 'expo-image';
import { getApiUrl } from '../utils/apiConfig';
import { isSupabasePublicVideoUrl } from '../utils/courseMediaUrls';

/** Matches ExternalBreaksCarousel / legacy MyCourseDetail default advance interval */
export const CAROUSEL_AUTO_MS = 5000;

const CAROUSEL_ARROW_HIT = 48;

export function resolveRemoteUri(url) {
  if (!url || typeof url !== 'string') return url;
  const s = url.trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) return `${getApiUrl()}${s}`;
  return s;
}

function parseYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const m =
    s.match(
      /(?:https?:\/\/)?(?:(?:www|m)\.)?youtube\.com\/(?:watch\?v=|embed\/|shorts\/)([^&?#/]+)/i
    ) || s.match(/(?:https?:\/\/)?youtu\.be\/([^&?#/]+)/i);
  return m ? m[1] : null;
}

function youtubeEmbedUrl(videoId) {
  if (!videoId) return '';
  const q = 'autoplay=1&playsinline=1&rel=0';
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${q}`;
}

function youtubeThumbUrl(videoId) {
  if (!videoId) return '';
  return `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
}

function CarouselRemoteImage({ uri, style }) {
  const resolved = resolveRemoteUri(uri);
  return (
    <Image source={{ uri: resolved }} style={style} contentFit="contain" transition={0} />
  );
}

/**
 * Style bundle shared by MyCourseDetailPage course gallery and Session Builder exercise modal carousel.
 */
export function createCourseMediaCarouselStyles(theme, slideWidth, slideHeight) {
  return {
    carouselOuter: {
      marginBottom: 20,
      alignSelf: 'stretch',
      width: '100%',
    },
    carouselRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    carouselStage: {
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#f0f0f0',
    },
    carouselArrowHit: {
      width: CAROUSEL_ARROW_HIT,
      minHeight: slideHeight,
      justifyContent: 'center',
      alignItems: 'center',
    },
    carouselArrowText: {
      fontSize: 38,
      lineHeight: 42,
      fontWeight: '200',
    },
    carouselListWeb: {},
    carouselSlide: {
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    carouselVideoOverlay: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.25)',
    },
    carouselPlayCircle: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: 'rgba(0,0,0,0.65)',
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: 4,
    },
    carouselPlayTriangle: {
      color: '#fff',
      fontSize: 28,
      fontWeight: '700',
    },
    carouselDots: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 10,
    },
    carouselDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      borderWidth: 1,
    },
    carouselDotSpacing: {
      marginLeft: 8,
    },
    carouselDotActive: {
      backgroundColor: theme.colors.heading,
    },
    carouselDotInactive: {
      backgroundColor: 'transparent',
    },
  };
}

/**
 * Horizontal media carousel (YouTube thumbs → modal via onOpenVideo, otherwise remote images).
 * Same UX as the top gallery on the individual course page.
 */
export default function CourseMediaCarousel({
  mediaUrls,
  slideWidth,
  slideHeight,
  theme,
  styles,
  onOpenVideo,
  onPressImage,
  pauseAutoRotate,
  showRemoveButton,
  onRemoveSlide,
  initialIndex = 0,
}) {
  const flatListRef = useRef(null);
  const scrollRef = useRef(null);
  const indexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const showArrows = mediaUrls.length > 1;

  useEffect(() => {
    if (mediaUrls.length === 0) return;
    const i = Math.min(Math.max(0, initialIndex), mediaUrls.length - 1);
    indexRef.current = i;
    setActiveIndex(i);
    if (Platform.OS === 'web') {
      scrollRef.current?.scrollTo({ x: i * slideWidth, animated: false });
    } else {
      try {
        flatListRef.current?.scrollToIndex({ index: i, animated: false });
      } catch {
        flatListRef.current?.scrollToOffset({ offset: i * slideWidth, animated: false });
      }
    }
  }, [initialIndex, mediaUrls.length, slideWidth]);

  const scrollToIndex = useCallback(
    (index) => {
      const i = ((index % mediaUrls.length) + mediaUrls.length) % mediaUrls.length;
      indexRef.current = i;
      setActiveIndex(i);
      if (Platform.OS === 'web') {
        scrollRef.current?.scrollTo({ x: i * slideWidth, animated: true });
      } else {
        try {
          flatListRef.current?.scrollToIndex({ index: i, animated: true });
        } catch {
          flatListRef.current?.scrollToOffset({
            offset: i * slideWidth,
            animated: true,
          });
        }
      }
    },
    [mediaUrls.length, slideWidth]
  );

  const goPrev = useCallback(() => {
    if (!showArrows) return;
    scrollToIndex(indexRef.current - 1);
  }, [showArrows, scrollToIndex]);

  const goNext = useCallback(() => {
    if (!showArrows) return;
    scrollToIndex(indexRef.current + 1);
  }, [showArrows, scrollToIndex]);

  const onMomentumEnd = useCallback(
    (e) => {
      const x = e.nativeEvent.contentOffset.x;
      const i = Math.round(x / Math.max(slideWidth, 1));
      indexRef.current = i;
      setActiveIndex(i);
    },
    [slideWidth]
  );

  const renderSlide = useCallback(
    (url, slideIndex) => {
      const videoId = parseYouTubeVideoId(url);
      if (videoId) {
        return (
          <TouchableOpacity
            style={[styles.carouselSlide, { width: slideWidth, height: slideHeight }]}
            onPress={() => onOpenVideo(youtubeEmbedUrl(videoId), 'Video')}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityLabel="Play video"
          >
            <CarouselRemoteImage uri={youtubeThumbUrl(videoId)} style={StyleSheet.absoluteFill} />
            <View style={styles.carouselVideoOverlay} pointerEvents="none">
              <View style={styles.carouselPlayCircle}>
                <Text style={styles.carouselPlayTriangle}>▶</Text>
              </View>
            </View>
          </TouchableOpacity>
        );
      }
      if (isSupabasePublicVideoUrl(url)) {
        const resolved = resolveRemoteUri(url);
        return (
          <TouchableOpacity
            style={[styles.carouselSlide, { width: slideWidth, height: slideHeight }]}
            onPress={() => onOpenVideo(resolved, 'Video')}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityLabel="Play video"
          >
            <View style={[StyleSheet.absoluteFillObject, { backgroundColor: '#000' }]} />
            <View style={styles.carouselVideoOverlay} pointerEvents="none">
              <View style={styles.carouselPlayCircle}>
                <Text style={styles.carouselPlayTriangle}>▶</Text>
              </View>
            </View>
          </TouchableOpacity>
        );
      }
      const imageSlide = (
        <CarouselRemoteImage uri={url} style={StyleSheet.absoluteFill} />
      );
      if (onPressImage) {
        return (
          <Pressable
            style={[styles.carouselSlide, { width: slideWidth, height: slideHeight }]}
            onPress={() => onPressImage(url, slideIndex)}
            accessibilityRole="button"
            accessibilityLabel="View larger image"
          >
            {imageSlide}
          </Pressable>
        );
      }
      return (
        <View style={[styles.carouselSlide, { width: slideWidth, height: slideHeight }]}>
          {imageSlide}
        </View>
      );
    },
    [slideWidth, slideHeight, onOpenVideo, onPressImage, styles]
  );

  const wrapWithRemove = useCallback(
    (slideEl, url, slideIndex) => {
      if (!showRemoveButton || !onRemoveSlide) return slideEl;
      return (
        <View style={{ width: slideWidth, height: slideHeight, position: 'relative' }}>
          {slideEl}
          <TouchableOpacity
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 20,
              width: 30,
              height: 30,
              borderRadius: 15,
              backgroundColor: theme.isDarkMode
                ? 'rgba(0,0,0,0.55)'
                : theme.colors.primary || '#d4af37',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onPress={() => onRemoveSlide(url, slideIndex)}
            accessibilityRole="button"
            accessibilityLabel="Remove image"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: '700', lineHeight: 20 }}>✕</Text>
          </TouchableOpacity>
        </View>
      );
    },
    [showRemoveButton, onRemoveSlide, slideWidth, slideHeight, theme.isDarkMode, theme.colors.primary]
  );

  useEffect(() => {
    if (mediaUrls.length <= 1 || pauseAutoRotate) return undefined;
    const tick = () => {
      scrollToIndex(indexRef.current + 1);
    };
    const id = setInterval(tick, CAROUSEL_AUTO_MS);
    return () => clearInterval(id);
  }, [mediaUrls.length, pauseAutoRotate, scrollToIndex]);

  if (!mediaUrls.length) return null;

  const arrowSpacer = !showArrows ? (
    <View style={{ width: CAROUSEL_ARROW_HIT, height: 1 }} />
  ) : null;

  const listWebStyle = [styles.carouselListWeb, { width: slideWidth, height: slideHeight }];

  return (
    <View style={styles.carouselOuter}>
      <View style={styles.carouselRow}>
        {showArrows ? (
          <TouchableOpacity
            style={styles.carouselArrowHit}
            onPress={goPrev}
            accessibilityRole="button"
            accessibilityLabel="Previous slide"
            hitSlop={{ top: 12, bottom: 12, left: 8, right: 8 }}
          >
            <Text style={[styles.carouselArrowText, { color: theme.colors.heading }]}>‹</Text>
          </TouchableOpacity>
        ) : (
          arrowSpacer
        )}
        <View
          style={[styles.carouselStage, { width: slideWidth }]}
          {...(Platform.OS === 'web' ? { dataSet: { courseCarousel: 'true' } } : {})}
        >
          {Platform.OS === 'web' ? (
            <ScrollView
              ref={scrollRef}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              style={listWebStyle}
              nestedScrollEnabled
              onMomentumScrollEnd={onMomentumEnd}
            >
              {mediaUrls.map((url, idx) => (
                <View key={`${idx}-${url}`} style={{ width: slideWidth, height: slideHeight }}>
                  {wrapWithRemove(renderSlide(url, idx), url, idx)}
                </View>
              ))}
            </ScrollView>
          ) : (
            <FlatList
              ref={flatListRef}
              data={mediaUrls}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              style={listWebStyle}
              keyExtractor={(item, index) => `${index}-${item}`}
              getItemLayout={(_, index) => ({
                length: slideWidth,
                offset: slideWidth * index,
                index,
              })}
              onMomentumScrollEnd={onMomentumEnd}
              onScrollToIndexFailed={(info) => {
                setTimeout(() => {
                  flatListRef.current?.scrollToIndex({
                    index: info.index,
                    animated: true,
                  });
                }, 100);
              }}
              renderItem={({ item, index }) => wrapWithRemove(renderSlide(item, index), item, index)}
              nestedScrollEnabled
            />
          )}
        </View>
        {showArrows ? (
          <TouchableOpacity
            style={styles.carouselArrowHit}
            onPress={goNext}
            accessibilityRole="button"
            accessibilityLabel="Next slide"
            hitSlop={{ top: 12, bottom: 12, left: 8, right: 8 }}
          >
            <Text style={[styles.carouselArrowText, { color: theme.colors.heading }]}>›</Text>
          </TouchableOpacity>
        ) : (
          arrowSpacer
        )}
      </View>
      {mediaUrls.length > 1 ? (
        <View style={styles.carouselDots}>
          {mediaUrls.map((_, i) => (
            <View
              key={String(i)}
              style={[
                styles.carouselDot,
                i === activeIndex ? styles.carouselDotActive : styles.carouselDotInactive,
                { borderColor: theme.colors.heading },
                ...(i > 0 ? [styles.carouselDotSpacing] : []),
              ]}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}
